import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/icons";
import { Flag } from "@/components/flag";
import { usePopoverPosition } from "@/components/use-popover-position";
import { ISO_COUNTRIES, countryMatchesQuery, isoCountryName } from "@/app/(app)/_lib/iso-countries";

type CountrySelectProps = {
  /** Selected ISO 3166-1 alpha-2 code (e.g. "DE"); "" when nothing is chosen. */
  value: string;
  onChange: (iso: string) => void;
  id?: string;
  placeholder?: string;
  /** Renders the trigger with the error border (paired with field-level error text). */
  invalid?: boolean;
  ariaLabel?: string;
  disabled?: boolean;
};

/**
 * Single-select, searchable country dropdown over the full ISO 3166-1 set
 * (`_lib/iso-countries`). Mirrors `CountryPickerPopover`'s look but is its own
 * component so the multi-select screening/adverse-media picker stays untouched.
 * Stores + emits the ISO code; shows flag + localized name.
 */
export function CountrySelect({
  value,
  onChange,
  id,
  placeholder = "Select a country",
  invalid = false,
  ariaLabel = "Country",
  disabled = false,
}: CountrySelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Hold the trigger in state (not a ref) so usePopoverPosition reads a real
  // value, never a ref during render — mirrors how popoverEl is tracked.
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [popoverEl, setPopoverEl] = useState<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pos = usePopoverPosition({ anchorEl, popoverEl, align: "stretch" });

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      const t = e.target as Node;
      if (anchorEl?.contains(t) || popoverEl?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, popoverEl, anchorEl]);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return ISO_COUNTRIES;
    return ISO_COUNTRIES.filter((c) => countryMatchesQuery(c.iso, q));
  }, [query]);

  function select(iso: string) {
    onChange(iso);
    setOpen(false);
    setQuery("");
  }

  return (
    <>
      <button
        ref={setAnchorEl}
        id={id}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={[
          "flex h-10 w-full items-center gap-2 rounded-[4px] border px-3 text-left text-small transition-colors",
          invalid ? "border-red-500" : "border-grey-200 hover:border-grey-500",
          disabled ? "cursor-not-allowed bg-grey-50 hover:border-grey-200" : "bg-white",
        ].join(" ")}
      >
        {value ? (
          <>
            <Flag iso={value} size={16} />
            <span className={`flex-1 truncate ${disabled ? "text-grey-500" : "text-black"}`}>
              {isoCountryName(value)}
            </span>
          </>
        ) : (
          <span className="flex-1 truncate text-grey-500">{placeholder}</span>
        )}
        <Icon name="expand_more" size={20} className="text-grey-500" />
      </button>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={setPopoverEl}
            role="listbox"
            aria-label="Countries"
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              width: pos?.width ?? 320,
              maxHeight: "min(320px, calc(100vh - 16px))",
              visibility: pos ? "visible" : "hidden",
            }}
            className="fixed z-[1400] flex flex-col gap-2 overflow-hidden rounded-[4px] border border-grey-200 bg-white p-2 shadow-soft"
          >
            <div className="flex items-center gap-1.5 rounded-[4px] bg-grey-100 px-2 py-1.5">
              <Icon name="search" size={20} className="text-grey-500" />
              <input
                ref={inputRef}
                type="search"
                placeholder="Search countries"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="min-w-0 flex-1 border-0 bg-transparent text-small text-black outline-none placeholder:text-grey-500"
              />
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="px-2 py-3 text-center text-small text-grey-500">
                  No countries match &ldquo;{query}&rdquo;.
                </p>
              ) : (
                filtered.map((c) => {
                  const isSelected = c.iso === value;
                  return (
                    <button
                      key={c.iso}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => select(c.iso)}
                      className={[
                        "flex items-center gap-2 rounded-[4px] px-2 py-1.5 text-left transition-colors",
                        isSelected
                          ? "bg-green-500-12 text-green-700 hover:bg-green-100"
                          : "text-grey-900 hover:bg-grey-50",
                      ].join(" ")}
                    >
                      <Flag iso={c.iso} size={16} />
                      <span className="flex-1 text-small font-medium">{c.name}</span>
                      {isSelected && <Icon name="check" size={20} className="text-green-700" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
